import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import PageHeader from '@/components/system/PageHeader';
import { AppBreadcrumb } from '@/components/system/AppBreadcrumb';
import { ContextBackButton } from '@/components/system/ContextBackButton';
import SectionCard from '@/components/system/SectionCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRkgComparison } from '@/hooks/useRkgComparison';
import { RkgComparisonPanel } from '@/components/risk/RkgComparisonPanel';
import {
  ArrowLeft,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  ShieldAlert,
  Activity,
  DollarSign,
  Clock,
  BarChart3,
  Repeat,
  Flame,
  Target,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ─── Types ──────────────────────────────────────────────────────────
interface ContractRanking {
  contract_id: string | null;
  contrato_nome: string | null;
  contratada: string | null;
  cluster_nome: string | null;
  score_criticidade: number | null;
  nivel_criticidade: string | null;
  prob_atraso_mc: number | null;
  prob_estouro_custo_mc: number | null;
  score_risco: number | null;
  tendencia: string | null;
  total_ocorrencias: number | null;
  taxa_reincidencia: number | null;
  exposicao_financeira: number | null;
  p80_duracao: number | null;
  p80_custo: number | null;
  ultima_simulacao: string | null;
  orcamento_total: number | null;
}

interface TrendPoint {
  contract_id: string | null;
  data_referencia: string | null;
  prob_atraso: number | null;
  prob_estouro: number | null;
  fonte_dados: string | null;
  tendencia_mc: string | null;
  delta_prob_atraso: number | null;
  delta_prob_estouro: number | null;
  p80_duracao: number | null;
  p80_custo: number | null;
}

interface UoHeatmap {
  uo_id: string | null;
  uo_nome: string | null;
  contract_id: string | null;
  score_risco_uo: number | null;
  nivel_calor: string | null;
  total_nao_conformidades: number | null;
  total_riscos_imediatos: number | null;
  total_inspecoes: number | null;
  densidade_nao_conformidade: number | null;
  progresso: number | null;
}

interface Anomaly {
  contract_id: string | null;
  contrato_nome: string | null;
  tipo_anomalia: string | null;
  descricao: string | null;
  severidade: string | null;
  detalhes: any;
  detectado_em: string | null;
}

interface McRun {
  id: string;
  contract_id: string;
  completed_at: string | null;
  n_simulations: number;
  resultado: any;
  status: string;
  tipo: string;
}

// Risk tokens — centralized from design system
import { riskColor as riskColorToken, riskBgColor, riskBorderColor, RISK_LABELS, riskLevelFromScore } from '@/lib/riskTokens';
const CRITICIDADE_COLORS: Record<string, string> = {
  critico: riskColorToken('critico'),
  alto: riskColorToken('alto'),
  moderado: riskColorToken('moderado'),
  baixo: riskColorToken('baixo'),
};

const CALOR_COLORS: Record<string, string> = {
  critico: riskBgColor('critico'),
  alto: riskBgColor('alto'),
  moderado: riskBgColor('moderado'),
  baixo: riskBgColor('baixo'),
};

const CALOR_BORDER: Record<string, string> = {
  critico: riskBorderColor('critico'),
  alto: riskBorderColor('alto'),
  moderado: riskBorderColor('moderado'),
  baixo: riskBorderColor('baixo'),
};

const CRITICIDADE_LABELS: Record<string, string> = {
  critico: 'Crítico',
  alto: 'Alto',
  moderado: 'Moderado',
  baixo: 'Baixo',
};

const ANOMALY_LABELS: Record<string, string> = {
  mc_outlier_historico: 'Resultado fora do padrão histórico',
  mc_outlier_global: 'Contrato fora da média global',
  indicador_salto_abrupto: 'Variação abrupta de indicador',
};

function TendenciaIcon({ tendencia, size = 'sm' }: { tendencia: string | null; size?: 'sm' | 'md' }) {
  const cls = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  if (tendencia === 'piorando') return <TrendingUp className={`${cls} text-destructive`} />;
  if (tendencia === 'melhorando') return <TrendingDown className={`${cls} text-[hsl(var(--success,142_71%_45%))]`} />;
  return <Minus className={`${cls} text-muted-foreground`} />;
}

function formatPercent(v: number | null | undefined): string {
  if (v == null) return '—';
  // Values from DB are already 0-100 scale
  return `${Number(v).toFixed(0)}%`;
}

function formatNumber(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('pt-BR');
}

function formatCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
  return `R$ ${v.toFixed(0)}`;
}

// ─── KPI Card ───────────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, variant, subtitle }: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  variant?: 'default' | 'destructive' | 'warning' | 'success';
  subtitle?: string;
}) {
  const colorMap = {
    default: 'text-foreground',
    destructive: 'text-destructive',
    warning: 'text-[hsl(var(--warning,30_90%_50%))]',
    success: 'text-[hsl(var(--success,142_71%_45%))]',
  };
  return (
    <div className="emp-card p-4 flex items-center gap-3">
      <div className="p-2 rounded-lg bg-muted">
        <Icon className={`h-5 w-5 ${colorMap[variant || 'default']}`} strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold tabular-nums leading-tight truncate">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
        {subtitle && <p className="text-xs text-muted-foreground/70">{subtitle}</p>}
      </div>
    </div>
  );
}

// ─── Trend Chart Tooltip ────────────────────────────────────────────
function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-mono">{typeof p.value === 'number' ? `${Number(p.value).toFixed(1)}%` : '—'}</span>
        </p>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
const RiskContractAnalysis: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();

  const [contract, setContract] = useState<ContractRanking | null>(null);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [uos, setUos] = useState<UoHeatmap[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [mcRun, setMcRun] = useState<McRun | null>(null);
  const [loading, setLoading] = useState(true);

  // RKG comparison data
  const { enabled: rkgEnabled, rkgData, backtestMetrics, useFallback } = useRkgComparison(contractId);

  useEffect(() => {
    if (!contractId) return;
    async function load() {
      setLoading(true);
      const [contractRes, trendsRes, uosRes, anomaliesRes, mcRes] = await Promise.all([
        supabase.from('vw_risk_contract_ranking').select('*').eq('contract_id', contractId).maybeSingle(),
        supabase.from('vw_risk_trends').select('*').eq('contract_id', contractId).order('data_referencia', { ascending: true }),
        supabase.from('vw_risk_uo_heatmap').select('*').eq('contract_id', contractId).order('score_risco_uo', { ascending: false }),
        supabase.from('vw_risk_anomalies').select('*').eq('contract_id', contractId).order('detectado_em', { ascending: false }).limit(5),
        supabase.from('risk_monte_carlo_runs').select('*').eq('contract_id', contractId).eq('status', 'completed').order('completed_at', { ascending: false }).limit(1).maybeSingle(),
      ]);
      setContract(contractRes.data as ContractRanking | null);
      setTrends((trendsRes.data || []) as TrendPoint[]);
      setUos((uosRes.data || []) as UoHeatmap[]);
      setAnomalies((anomaliesRes.data || []) as Anomaly[]);
      setMcRun(mcRes.data as McRun | null);
      setLoading(false);
    }
    load();
  }, [contractId]);

  // ── MC resultado parsed
  const mcData = useMemo(() => {
    if (!mcRun?.resultado) return null;
    const r = typeof mcRun.resultado === 'string' ? JSON.parse(mcRun.resultado) : mcRun.resultado;
    return {
      p50_prazo: r?.p50_prazo ?? r?.provavel_prazo ?? null,
      p80_prazo: r?.p80_prazo ?? r?.pior_prazo ?? null,
      prob_atraso: r?.prob_atraso ?? null,
      prob_estouro: r?.prob_estouro ?? r?.prob_estouro_custo ?? null,
      n_simulations: mcRun.n_simulations,
      completed_at: mcRun.completed_at,
    };
  }, [mcRun]);

  // ── Trend chart data
  const chartData = useMemo(() => {
    return trends.map(t => ({
      date: t.data_referencia ? format(new Date(t.data_referencia), 'dd/MM', { locale: ptBR }) : '',
      prob_atraso: t.prob_atraso,
      prob_estouro: t.prob_estouro,
    }));
  }, [trends]);

  // ── Skeleton state
  if (loading) {
    return (
      <div className="space-y-6 p-1">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-7 w-64" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-[10px]" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-[320px] rounded-[10px] lg:col-span-2" />
          <Skeleton className="h-[320px] rounded-[10px]" />
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="space-y-6 p-1">
        <ContextBackButton to="/empreendimentos/inteligencia" label="Inteligência de Risco" />
        <PageHeader title="Contrato não encontrado" subtitle="Nenhum dado analítico disponível para este contrato" />
      </div>
    );
  }

  const nivel = contract.nivel_criticidade || 'baixo';
  const score = contract.score_criticidade ?? 0;

  return (
    <div className="space-y-6 p-1">
      <AppBreadcrumb items={[
        { label: 'Empreendimentos', href: '/empreendimentos' },
        { label: 'Inteligência de Risco', href: '/empreendimentos/inteligencia' },
        { label: contract.contrato_nome || 'Contrato' },
      ]} />
      <ContextBackButton to="/empreendimentos/inteligencia" label="Inteligência de Risco" />
      {/* ── Anomaly Banner ──────────────────────────────── */}
      {anomalies.length > 0 && (
        <div
          className="rounded-[10px] border p-4 flex items-start gap-3"
          style={{
            backgroundColor: anomalies[0].severidade === 'critico' ? 'hsl(var(--destructive) / 0.08)' : 'hsl(var(--warning, 30 90% 50%) / 0.08)',
            borderColor: anomalies[0].severidade === 'critico' ? 'hsl(var(--destructive) / 0.3)' : 'hsl(var(--warning, 30 90% 50%) / 0.3)',
          }}
        >
          <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium">
              {anomalies.length === 1
                ? ANOMALY_LABELS[anomalies[0].tipo_anomalia || ''] || anomalies[0].descricao
                : `${anomalies.length} anomalias detectadas neste contrato`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {anomalies.map(a => ANOMALY_LABELS[a.tipo_anomalia || ''] || a.descricao).join(' · ')}
            </p>
          </div>
          <Badge variant={anomalies[0].severidade === 'critico' ? 'destructive' : 'outline'} className="text-xs flex-shrink-0">
            {anomalies[0].severidade}
          </Badge>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/empreendimentos/inteligencia')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-[22px] font-semibold text-foreground font-display leading-tight truncate">
              {contract.contrato_nome}
            </h2>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => navigate(`/empreendimentos/contratos/${contractId}`)}
            >
              Ver Contrato
            </Button>
            <Badge
              variant={nivel === 'critico' ? 'destructive' : 'outline'}
              className="text-xs"
              style={
                nivel !== 'critico'
                  ? { borderColor: CRITICIDADE_COLORS[nivel], color: CRITICIDADE_COLORS[nivel] }
                  : undefined
              }
            >
              {CRITICIDADE_LABELS[nivel]}
            </Badge>
            <TendenciaIcon tendencia={contract.tendencia} size="md" />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {contract.contratada} · {contract.cluster_nome}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums" style={{ color: CRITICIDADE_COLORS[nivel] }}>
              {score}
            </p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Score</p>
          </div>
        </div>
      </div>

      {/* ── KPI Row ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          label="Score de Risco"
          value={contract.score_risco != null ? `${contract.score_risco.toFixed(0)}` : '—'}
          icon={ShieldAlert}
          variant={contract.score_risco != null && contract.score_risco >= 70 ? 'destructive' : 'default'}
        />
        <KpiCard
          label="Prob. de Atraso"
          value={formatPercent(contract.prob_atraso_mc)}
          icon={Clock}
          variant={contract.prob_atraso_mc != null && contract.prob_atraso_mc >= 70 ? 'destructive' : contract.prob_atraso_mc != null && contract.prob_atraso_mc >= 40 ? 'warning' : 'default'}
        />
        <KpiCard
          label="Prob. Estouro Custo"
          value={formatPercent(contract.prob_estouro_custo_mc)}
          icon={DollarSign}
          variant={contract.prob_estouro_custo_mc != null && contract.prob_estouro_custo_mc >= 50 ? 'destructive' : 'default'}
        />
        <KpiCard
          label="Ocorrências"
          value={formatNumber(contract.total_ocorrencias)}
          icon={Activity}
        />
        <KpiCard
          label="Reincidência"
          value={contract.taxa_reincidencia != null ? `${(contract.taxa_reincidencia * 100).toFixed(0)}%` : '—'}
          icon={Repeat}
          variant={contract.taxa_reincidencia != null && contract.taxa_reincidencia >= 0.3 ? 'warning' : 'default'}
        />
        <KpiCard
          label="Exposição Financeira"
          value={formatCurrency(contract.exposicao_financeira)}
          icon={DollarSign}
          variant={contract.exposicao_financeira != null && contract.exposicao_financeira > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* ── Main Content: Trend + MC Snapshot ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Trend Chart */}
        <SectionCard
          title="Evolução do Risco"
          icon={BarChart3}
          className="lg:col-span-2"
        >
          {chartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Activity className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">Sem dados de tendência disponíveis</p>
              <p className="text-xs mt-1">Simulações Monte Carlo e indicadores geram a série temporal</p>
            </div>
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradAtraso" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradEstouro" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--warning, 30 90% 50%))" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="hsl(var(--warning, 30 90% 50%))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    className="text-muted-foreground"
                    tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                    domain={[0, 1]}
                  />
                  <Tooltip content={<TrendTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="prob_atraso"
                    name="Prob. Atraso"
                    stroke="hsl(var(--destructive))"
                    fill="url(#gradAtraso)"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  <Area
                    type="monotone"
                    dataKey="prob_estouro"
                    name="Prob. Estouro"
                    stroke="hsl(var(--warning, 30 90% 50%))"
                    fill="url(#gradEstouro)"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 -mt-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 rounded-full bg-destructive" />
                  <span className="text-xs text-muted-foreground">Prob. Atraso</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 rounded-full bg-[hsl(var(--warning,30_90%_50%))]" />
                  <span className="text-xs text-muted-foreground">Prob. Estouro</span>
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* Monte Carlo Snapshot */}
        <SectionCard title="Simulação Monte Carlo" icon={Target}>
          {!mcData ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Target className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">Nenhuma simulação concluída</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">P50 Prazo</p>
                  <p className="text-lg font-bold tabular-nums">{mcData.p50_prazo != null ? `${mcData.p50_prazo} dias` : '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">P80 Prazo</p>
                  <p className="text-lg font-bold tabular-nums">{mcData.p80_prazo != null ? `${mcData.p80_prazo} dias` : '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Prob. Atraso</p>
                  <p className="text-lg font-bold tabular-nums text-destructive">{formatPercent(mcData.prob_atraso)}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Prob. Estouro</p>
                  <p className="text-lg font-bold tabular-nums text-[hsl(var(--warning,30_90%_50%))]">{formatPercent(mcData.prob_estouro)}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground text-center border-t pt-3">
                <p>{formatNumber(mcData.n_simulations)} simulações</p>
                {mcData.completed_at && (
                  <p className="mt-0.5">
                    Última execução: {format(new Date(mcData.completed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                )}
              </div>
            </div>
          )}
        </SectionCard>

        {/* RKG Comparison Panel */}
        {rkgEnabled && rkgData && (
          <RkgComparisonPanel
            rkgData={rkgData}
            legacyData={mcData ? {
              p50_prazo: mcData.p50_prazo,
              p80_prazo: mcData.p80_prazo,
              prob_atraso: mcData.prob_atraso,
              prob_estouro: mcData.prob_estouro,
            } : null}
            backtestMetrics={backtestMetrics}
            useFallback={useFallback}
          />
        )}
      </div>

      {/* ── UO Heatmap ───────────────────────────────────── */}
      <SectionCard title="Unidades Operacionais — Mapa de Risco" icon={Flame}>
        {uos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Flame className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">Nenhuma unidade operacional vinculada</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {uos.map((uo) => {
              const heat = uo.nivel_calor || 'baixo';
              return (
                <button
                  key={uo.uo_id}
                  onClick={() => navigate(`/empreendimentos/inteligencia/${contractId}/uo/${uo.uo_id}`)}
                  className="text-left p-4 rounded-[10px] border transition-all hover:shadow-md group"
                  style={{
                    backgroundColor: CALOR_COLORS[heat],
                    borderColor: CALOR_BORDER[heat],
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium truncate">{uo.uo_nome || 'UO sem nome'}</span>
                    <Badge
                      variant={heat === 'critico' ? 'destructive' : 'outline'}
                      className="text-xs"
                      style={heat !== 'critico' ? { borderColor: CRITICIDADE_COLORS[heat], color: CRITICIDADE_COLORS[heat] } : undefined}
                    >
                      {CRITICIDADE_LABELS[heat] || heat}
                    </Badge>
                  </div>
                  {/* Score bar */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-1.5 rounded-full bg-background/50 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(uo.score_risco_uo ?? 0, 100)}%`, backgroundColor: CRITICIDADE_COLORS[heat] }}
                      />
                    </div>
                    <span className="text-xs font-mono font-medium tabular-nums w-6 text-right">{uo.score_risco_uo?.toFixed(0) ?? '—'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-sm font-semibold tabular-nums">{uo.total_nao_conformidades ?? 0}</p>
                      <p className="text-xs text-muted-foreground">NC</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold tabular-nums">{uo.total_riscos_imediatos ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Riscos</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold tabular-nums">{uo.total_inspecoes ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Inspeções</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default RiskContractAnalysis;
